import { z } from 'zod';
import { DVF_PROPERTY_TYPES, inseeCodeSchema } from './market.js';
import { idSchema, timestampSchema } from './common.js';
/**
 * Lot 15 — analyse de secteur : loyers (carte des loyers ANIL), rendement brut, moteur de règles
 * à seuils (méthode M5) et liste de suivi des communes (entonnoir M4).
 */
// --- Loyers ---------------------------------------------------------------------------------
export const RENT_PREDICTION_KINDS = ['commune', 'maille'];
export const rentSchema = z.object({
    /** Loyer d'annonce prédit, charges comprises, en centimes par m² et par mois. */
    rentPerM2Cents: z.number().int().nonnegative(),
    /** Bornes de l'intervalle de prédiction, mêmes unités. */
    lowerCents: z.number().int().nonnegative(),
    upperCents: z.number().int().nonnegative(),
    /** `commune` : estimé sur les annonces de la commune ; `maille` : emprunté à une zone plus large. */
    predictionKind: z.enum(RENT_PREDICTION_KINDS),
    observations: z.number().int().nonnegative(),
    reliable: z.boolean(),
    source: z.string(),
});
// --- Moteur de règles -----------------------------------------------------------------------
export const STRATEGIES = [
    'resale-to-investors',
    'renovation-resale',
    'secondary-residence',
    'building-split',
    'land-division',
];
export const STRATEGY_VERDICTS = ['feasible', 'difficult', 'not-evaluable'];
export const strategyResultSchema = z.object({
    strategy: z.enum(STRATEGIES),
    label: z.string(),
    verdict: z.enum(STRATEGY_VERDICTS),
    reason: z.string(),
});
export const sectorThresholdsSchema = z.object({
    /** Rendement brut minimal pour la revente à investisseurs locatifs. */
    minGrossYield: z.number(),
    /** Prix médian minimal, en €/m², sous lequel les travaux mangent la marge d'une rénovation-revente. */
    minRenovationPricePerM2: z.number(),
});
export const sectorAnalysisSchema = z.object({
    inseeCode: inseeCodeSchema,
    communeName: z.string(),
    propertyType: z.enum(DVF_PROPERTY_TYPES),
    /** Dernière année DVF fiable, sur laquelle reposent les prix ; `null` si aucune. */
    referenceYear: z.number().int().nullable(),
    /** €/m² bâti, arrondis à l'euro. */
    referencePricePerM2: z.number().int().nullable(),
    p25PricePerM2: z.number().int().nullable(),
    p75PricePerM2: z.number().int().nullable(),
    rent: rentSchema.nullable(),
    /** Loyer annuel rapporté au prix médian ; `null` sans prix ou sans loyer. */
    grossYield: z.number().nullable(),
    annualGrowth: z.number().nullable(),
    strategies: z.array(strategyResultSchema),
    caveats: z.array(z.string()),
});
export const analysisQuerySchema = z.object({
    codes: z
        .string()
        .transform((value) => value.split(',').map((code) => code.trim()).filter((code) => code !== ''))
        .pipe(z.array(inseeCodeSchema).min(1).max(5)),
    propertyType: z.enum(DVF_PROPERTY_TYPES).default('Appartement'),
});
/** Une commune peut échouer (fichier absent, source injoignable) sans faire échouer les autres. */
export const analysisItemSchema = z.object({
    inseeCode: inseeCodeSchema,
    status: z.enum(['ok', 'error']),
    error: z.string().nullable(),
    analysis: sectorAnalysisSchema.nullable(),
});
export const analysisResponseSchema = z.object({
    thresholds: sectorThresholdsSchema,
    items: z.array(analysisItemSchema),
});
// --- Liste de suivi (entonnoir M4) ---------------------------------------------------------
/** Balayage large → présélection → comparaison fine → terrain. */
export const WATCHLIST_STAGES = ['sweep', 'shortlist', 'comparison', 'field'];
export const watchlistInputSchema = z.object({
    inseeCode: inseeCodeSchema,
    communeName: z.string().trim().min(1).max(120),
    department: z.string().trim().max(3).default(''),
    stage: z.enum(WATCHLIST_STAGES).default('sweep'),
    note: z.string().trim().max(1000).default(''),
});
export const watchlistUpdateSchema = z
    .object({
    stage: z.enum(WATCHLIST_STAGES),
    note: z.string().trim().max(1000),
    /** Une commune écartée est archivée, jamais supprimée. */
    archived: z.boolean(),
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Aucune modification' });
export const watchlistItemSchema = z.object({
    id: idSchema,
    inseeCode: inseeCodeSchema,
    communeName: z.string(),
    department: z.string(),
    stage: z.enum(WATCHLIST_STAGES),
    note: z.string(),
    archivedAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
});
//# sourceMappingURL=sector.js.map